import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardFooter, Col, Container, Row } from "reactstrap";
import Flatpickr from "react-flatpickr";
import WeeklyUploads from "./WeeklyUploads";
import WeeklyProfitsByChannel from "./WeeklyProfitsByChannel";
import WeeklyNewSuppliers from "./WeeklyNewSuppliers";
import WeeklyTotalOrderAmount from "./WeeklyTotalOrderAmount";
//import WeeklySuppliersTotalCancelled from "./WeeklySuppliersTotalCancelled";
import WeeklyTotalMemberPurchases from "./WeeklyTotalMemberPurchases";
import SubscriptionStats from "./SubscriptionStats";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
  getUserStats,
  getWeeklyGeneralCustomerOrderCounts,
  getMonthlyGeneralCustomerOrderCounts,
  getWeeklyGeneralSupplierOrderCounts,
  getGeneralSupplierOrderCounts,
  getInventoryHistory,
  getWeeklyTotalPurchases,
} from "../../slices/thunks";
import CountUp from "react-countup";
import TopProductMargins from "./TopProductMargins";
// import TopSellerSuppliers from "./TopSellerSuppliers";
import WeeklyRefunds from "./WeeklyRefunds";
// import TopSellerProducts from "./TopSellerProducts";
import { Link } from "react-router-dom";
import {
  DASHBOARD_DATE_FILTER_PERIODS,
  formatDateParam,
  getDashboardFilterParams,
  shouldUseMonthlyEndpoint,
} from "./dateFilterUtils";
import {
  getWeeklyGeneralCustomerOrderCounts as getWeeklyGeneralCustomerOrderCountsApi,
  getGeneralSupplierOrderCounts as getGeneralSupplierOrderCountsApi,
} from "../../apiCalls/caller";
//import WeeklyCustomerTotalCancelled from "./WeeklyCustomersTotalCancelled";

const getStatsPeriodLabel = (period) => {
  switch (period) {
    case DASHBOARD_DATE_FILTER_PERIODS.WEEK:
      return "This Week";
    case DASHBOARD_DATE_FILTER_PERIODS.MONTH:
      return "This Month";
    case DASHBOARD_DATE_FILTER_PERIODS.YEAR:
      return "This Year";
    case DASHBOARD_DATE_FILTER_PERIODS.CUSTOM:
      return "Selected Range";
    case DASHBOARD_DATE_FILTER_PERIODS.ALL:
    default:
      return "All Time";
  }
};

const getPreviousPeriodLabel = (period) => {
  switch (period) {
    case DASHBOARD_DATE_FILTER_PERIODS.WEEK:
      return "previous week";
    case DASHBOARD_DATE_FILTER_PERIODS.MONTH:
      return "previous month";
    default:
      return "previous period";
  }
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getNumber = (value) => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getFirstNumber = (values = []) => {
  const foundValue = values.find(
    (value) => value !== undefined && value !== null && !Number.isNaN(Number(value))
  );

  return getNumber(foundValue);
};

const sumWeeklyField = (items, field) => {
  if (!Array.isArray(items)) return 0;

  return items.reduce((total, item) => total + getNumber(item?.[field]), 0);
};

const sumWeeklyFirstNumber = (items, fields) => {
  if (!Array.isArray(items)) return 0;

  return items.reduce(
    (total, item) =>
      total + getFirstNumber(fields.map((field) => item?.[field])),
    0
  );
};

const Dashboard = () => {
  document.title = "Dashboard | Buyers Club";
  const dispatch = useDispatch();

  const selectDashboardData = createSelector(
    (state) => state.Dashboard,
    (userStats) => userStats
  );
  // Inside your component
  const {
    userStats,
    weeklyGeneralCustomerOrderCounts,
    weeklyGeneralSupplierOrderCounts,
    generalSupplierOrderCounts,
  } = useSelector(selectDashboardData);
  const [dateFilter, setDateFilter] = useState({
    period: DASHBOARD_DATE_FILTER_PERIODS.WEEK,
    customDateFrom: null,
    customDateTo: null,
  });
  const dateFilterParams = useMemo(
    () => getDashboardFilterParams(dateFilter),
    [dateFilter]
  );
  const chartResetKey = useMemo(
    () =>
      [
        dateFilter.period,
        dateFilter.customDateFrom?.getTime() ?? "null",
        dateFilter.customDateTo?.getTime() ?? "null",
      ].join("-"),
    [
      dateFilter.customDateFrom,
      dateFilter.customDateTo,
      dateFilter.period,
    ]
  );
  const statsPeriodLabel = getStatsPeriodLabel(dateFilter.period);
  const totalUserLogins = Number(userStats?.total_logins_last_30) || 0;
  const totalBpaLogins = Number(userStats?.total_logins_bpa) || 0;
  const totalLogins = totalUserLogins + totalBpaLogins;
  const totalBpaLoginsPercentage =
    totalLogins > 0 ? ((totalBpaLogins / totalLogins) * 100).toFixed(0) : "0";
  const subscriptionStats = [
    {
      key: "new",
      label: "New Subscriptions",
      current: userStats?.total_new_subscriptions_30 || 0,
      previous: userStats?.total_new_subscriptions_60 || 0,
      yearly: userStats?.total_new_subscriptions_yearly || 0,
      monthly: userStats?.total_new_subscriptions_monthly || 0,
      download: 2,
      positiveWhenLower: false,
    },
    {
      key: "cancelled",
      label: "Cancelled Subscriptions",
      current: userStats?.total_del_subscriptions_30 || 0,
      previous: userStats?.total_del_subscriptions_60 || 0,
      yearly: userStats?.total_del_subscriptions_yearly || 0,
      monthly: userStats?.total_del_subscriptions_monthly || 0,
      download: 3,
      positiveWhenLower: true,
    },
    {
      key: "renewed",
      label: "Renewed Subscriptions",
      current: userStats?.total_ren_subscriptions_30 || 0,
      previous: userStats?.total_ren_subscriptions_60 || 0,
      yearly: userStats?.total_ren_subscriptions_yearly || 0,
      monthly: userStats?.total_ren_subscriptions_monthly || 0,
      download: 4,
      positiveWhenLower: false,
    },
  ];
  const weeklyMemberPurchases = sumWeeklyField(
    weeklyGeneralCustomerOrderCounts,
    "orderedCount"
  );
  const weeklyBpaPurchases = sumWeeklyField(
    weeklyGeneralCustomerOrderCounts,
    "bpaOrderedCount"
  );
  const weeklyBpaPurchasesPercentage =
    weeklyMemberPurchases > 0
      ? ((weeklyBpaPurchases / weeklyMemberPurchases) * 100).toFixed(0)
      : "0";
  const weeklyTotalSpent = sumWeeklyField(
    weeklyGeneralCustomerOrderCounts,
    "totalPrice"
  );
  const weeklyBpaAmountSpent = sumWeeklyFirstNumber(
    weeklyGeneralCustomerOrderCounts,
    ["bpaTotalPrice", "bpaTotalAmount", "bpaPrice", "bpaAmount"]
  );
  const weeklyBpaAmountPercentage =
    weeklyTotalSpent > 0
      ? ((weeklyBpaAmountSpent / weeklyTotalSpent) * 100).toFixed(0)
      : "0";
  const weeklyCompletedOrders = sumWeeklyField(
    weeklyGeneralSupplierOrderCounts,
    "orderedCount"
  );
  const weeklyCancelledOrders = sumWeeklyField(
    weeklyGeneralSupplierOrderCounts,
    "cancelledCount"
  );
  const weeklyTotalOrders = weeklyCompletedOrders + weeklyCancelledOrders;
  const weeklyCancelledOrdersPercentage =
    weeklyTotalOrders > 0
      ? ((weeklyCancelledOrders / weeklyTotalOrders) * 100).toFixed(0)
      : "0";

  // New GeneralSupplierOrderCounts (aggregated, non-weekly)
  const supplierOrderedCount = Number(generalSupplierOrderCounts?.orderedCount) || 0;
  const supplierCancelledCount = Number(generalSupplierOrderCounts?.cancelledCount) || 0;
  const supplierCancelledMembersOrders = Number(generalSupplierOrderCounts?.cancelledMembersOrders) || 0;
  const supplierTotalOrders = supplierOrderedCount + supplierCancelledCount;
  const supplierCancelledPercentage =
    supplierTotalOrders > 0
      ? ((supplierCancelledCount / supplierTotalOrders) * 100).toFixed(0)
      : "0";
  const supplierMembersCancelledPercentage =
    supplierTotalOrders > 0
      ? ((supplierCancelledMembersOrders / supplierTotalOrders) * 100).toFixed(0)
      : "0";

  useEffect(() => {
    dispatch(getUserStats(dateFilterParams));
    dispatch(getGeneralSupplierOrderCounts(dateFilterParams));
  }, [dateFilterParams, dispatch]);

  // Previous period data for month-over-month comparison
  const [previousPurchasesData, setPreviousPurchasesData] = useState(null);
  const [previousSupplierOrdersData, setPreviousSupplierOrdersData] =
    useState(null);

  useEffect(() => {
    const isWeek = dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.WEEK;
    const isMonth = dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.MONTH;

    if (isWeek || isMonth) {
      const today = new Date();
      const daysOffset = isWeek ? 7 : 30;
      const prevDateTo = new Date(today);
      prevDateTo.setDate(today.getDate() - daysOffset);
      const prevDateFrom = new Date(prevDateTo);
      prevDateFrom.setDate(prevDateTo.getDate() - daysOffset);

      const prevParams = {
        dateFrom: formatDateParam(prevDateFrom),
        dateTo: formatDateParam(prevDateTo),
      };

      getWeeklyGeneralCustomerOrderCountsApi(prevParams)
        .then((res) => {
          setPreviousPurchasesData(res?.data || null);
        })
        .catch(() => setPreviousPurchasesData(null));

      getGeneralSupplierOrderCountsApi(prevParams)
        .then((res) => {
          setPreviousSupplierOrdersData(res?.data || null);
        })
        .catch(() => setPreviousSupplierOrdersData(null));
    } else {
      setPreviousPurchasesData(null);
      setPreviousSupplierOrdersData(null);
    }
  }, [dateFilterParams, dateFilter.period]);

  // Previous period computations for Purchases
  const previousMemberPurchases = sumWeeklyField(
    previousPurchasesData,
    "orderedCount"
  );
  const previousTotalSpent = sumWeeklyField(
    previousPurchasesData,
    "totalPrice"
  );
  const purchasesMemberDiff = weeklyMemberPurchases - previousMemberPurchases;
  const purchasesAmountSpentDiff = weeklyTotalSpent - previousTotalSpent;

  // Previous period computations for Supplier Orders
  const previousSupplierOrderedCount =
    Number(previousSupplierOrdersData?.orderedCount) || 0;
  const previousSupplierCancelledCount =
    Number(previousSupplierOrdersData?.cancelledCount) || 0;
  const previousSupplierTotalOrders =
    previousSupplierOrderedCount + previousSupplierCancelledCount;
  const supplierTotalOrdersDiff =
    supplierTotalOrders - previousSupplierTotalOrders;
  const supplierCancelledDiff =
    supplierCancelledCount - previousSupplierCancelledCount;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col>
              <Card>
                <CardBody className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                  <div>
                    <h4 className="mb-1">Dashboard Date Filter</h4>
                    <p className="text-muted mb-0">
                      Applies to all dashboard charts and summary statistics.
                    </p>
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <button
                      type="button"
                      className={`btn btn-soft-${
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.ALL
                          ? "success"
                          : "secondary"
                      } btn-sm shadow-none`}
                      onClick={() => {
                        setDateFilter({
                          period: DASHBOARD_DATE_FILTER_PERIODS.ALL,
                          customDateFrom: null,
                          customDateTo: null,
                        });
                      }}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`btn btn-soft-${
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.WEEK
                          ? "success"
                          : "secondary"
                      } btn-sm shadow-none`}
                      onClick={() => {
                        setDateFilter({
                          period: DASHBOARD_DATE_FILTER_PERIODS.WEEK,
                          customDateFrom: null,
                          customDateTo: null,
                        });
                      }}
                    >
                      Week
                    </button>
                    <button
                      type="button"
                      className={`btn btn-soft-${
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.MONTH
                          ? "success"
                          : "secondary"
                      } btn-sm shadow-none`}
                      onClick={() => {
                        setDateFilter({
                          period: DASHBOARD_DATE_FILTER_PERIODS.MONTH,
                          customDateFrom: null,
                          customDateTo: null,
                        });
                      }}
                    >
                      Month
                    </button>
                    <button
                      type="button"
                      className={`btn btn-soft-${
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.YEAR
                          ? "success"
                          : "secondary"
                      } btn-sm shadow-none`}
                      onClick={() => {
                        setDateFilter({
                          period: DASHBOARD_DATE_FILTER_PERIODS.YEAR,
                          customDateFrom: null,
                          customDateTo: null,
                        });
                      }}
                    >
                      Year
                    </button>
                    <button
                      type="button"
                      className={`btn btn-soft-${
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.CUSTOM
                          ? "success"
                          : "secondary"
                      } btn-sm shadow-none`}
                      onClick={() => {
                        setDateFilter((prevState) => ({
                          ...prevState,
                          period: DASHBOARD_DATE_FILTER_PERIODS.CUSTOM,
                        }));
                      }}
                    >
                      Custom
                    </button>
                    {dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.CUSTOM && (
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <Flatpickr
                          className="form-control form-control-sm"
                          placeholder="Select a date from"
                          value={dateFilter.customDateFrom}
                          onChange={(selectedDates) => {
                            setDateFilter((prevState) => ({
                              ...prevState,
                              customDateFrom: selectedDates[0] || null,
                            }));
                          }}
                          style={{ width: "140px", minWidth: "140px" }}
                          options={{
                            maxDate: dateFilter.customDateTo || undefined,
                            mode: "single",
                            dateFormat: "m-d-Y",
                          }}
                        />
                        <span>to</span>
                        <Flatpickr
                          className="form-control form-control-sm"
                          placeholder="Select a date to"
                          value={dateFilter.customDateTo}
                          onChange={(selectedDates) => {
                            setDateFilter((prevState) => ({
                              ...prevState,
                              customDateTo: selectedDates[0] || null,
                            }));
                          }}
                          style={{ width: "140px", minWidth: "140px" }}
                          options={{
                            minDate: dateFilter.customDateFrom || undefined,
                            mode: "single",
                            dateFormat: "m-d-Y",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          {userStats && (
            <Row>
              <Col xl={3} md={6}>
                <Card className="card-height-100">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 ps-3">
                        <h5 className="text-muted text-uppercase fs-13 mb-0">
                          Total Logins {statsPeriodLabel}
                        </h5>
                      </div>
                    </div>
                    <div className="mt-4 mb-2 pt-1 ps-4">
                      <p className="mb-2 text-muted">
                        <span className="fw-medium text-dark">Total logins:</span>{" "}
                        <span className="counter-value text-dark">
                          <CountUp
                            start={0}
                            end={totalLogins}
                            decimals={0}
                            duration={4}
                          />
                        </span>
                      </p>
                      <p className="mb-0 text-muted">
                        <span className="fw-medium text-dark">BPA users:</span>{" "}
                        {totalBpaLogins} ({totalBpaLoginsPercentage}%)
                      </p>

                      {(dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.MONTH ||
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.WEEK) && (
                        <p className="mt-3 mb-0 text-muted">
                          <span
                            className={`badge bg-${
                              userStats.total_logins_last_30 -
                                userStats.total_logins_last_60 >=
                              0
                                ? "success"
                                : "danger"
                            }-subtle text-${
                              userStats.total_logins_last_30 -
                                userStats.total_logins_last_60 >=
                              0
                                ? "success"
                                : "danger"
                            } mb-0 me-1`}
                          >
                            <i
                              className={`ri-arrow-${
                                userStats.total_logins_last_30 -
                                  userStats.total_logins_last_60 >=
                                0
                                  ? "up"
                                  : "down"
                              }-line align-middle me-1`}
                            ></i>
                            {userStats.total_logins_last_30 -
                              userStats.total_logins_last_60} vs. {getPreviousPeriodLabel(dateFilter.period)}
                          </span>
                        </p>
                      )}
                    </div>
                  </CardBody>
                  <CardFooter className="text-center">
                    <Link
                      onClick={() =>
                        dispatch(getUserStats({ ...dateFilterParams, download: true }))
                      }
                      className="text-decoration-none mt-2"
                    >
                      <i className="ri-download-2-line align-middle me-2"></i>
                      Download details
                    </Link>
                  </CardFooter>
                </Card>
              </Col>
              <Col xl={3} md={6}>
                <Card className="card-height-100">
                  <CardBody>
                    <div className="d-flex align-items-center mb-4">
                      <div className="flex-grow-1 ps-3">
                        <h5 className="text-muted text-uppercase fs-13 mb-0">
                          Subscriptions {statsPeriodLabel}
                        </h5>
                      </div>
                    </div>
                    <div className="ps-4">
                      {subscriptionStats.map((subscription) => {
                        const difference = subscription.current - subscription.previous;
                        const isPositive = subscription.positiveWhenLower
                          ? difference < 0
                          : difference >= 0;

                        return (
                          <div
                            key={subscription.key}
                            className="mb-3 text-muted"
                          >
                            <p className="mb-1 d-flex flex-wrap align-items-center gap-1">
                              <span className="fw-medium text-dark">{subscription.label}:</span>
                              <span className="counter-value text-dark">
                                <CountUp
                                  start={0}
                                  end={subscription.current}
                                  decimals={0}
                                  duration={4}
                                />
                              </span>
                            </p>
                            <p className="mb-1 small text-muted">
                              Annual: {subscription.yearly} | Monthly: {subscription.monthly}
                            </p>
                            {(dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.MONTH ||
                              dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.WEEK) && (
                              <span
                                className={`badge bg-${
                                  isPositive ? "success" : "danger"
                                }-subtle text-${
                                  isPositive ? "success" : "danger"
                                }`}
                              >
                                <i
                                  className={`ri-arrow-${
                                    difference > 0 ? "up" : "down"
                                  }-line align-middle me-1`}
                                ></i>
                                {difference} vs. {getPreviousPeriodLabel(dateFilter.period)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardBody>
                  <CardFooter className="text-center">
                    <Link
                      onClick={() => {
                        dispatch(
                          getUserStats({
                            ...dateFilterParams,
                            download: true,
                          })
                        );
                      }}
                      className="text-decoration-none"
                    >
                      <i className="ri-download-2-line align-middle me-2"></i>
                      Download details
                    </Link>
                  </CardFooter>
                </Card>
              </Col>
              <Col xl={3} md={6}>
                <Card className="card-height-100">
                  <CardBody>
                    <div className="d-flex align-items-center mb-4">
                      <div className="flex-grow-1 ps-3">
                        <h5 className="text-muted text-uppercase fs-13 mb-0">
                          Purchases {statsPeriodLabel}
                        </h5>
                      </div>
                    </div>
                    <div className="ps-4">
                      <p className="mb-1 text-muted">
                        <span className="fw-medium text-dark">Member purchases:</span>{" "}
                        <span className="counter-value text-dark">
                          <CountUp
                            start={0}
                            end={weeklyMemberPurchases}
                            decimals={0}
                            duration={4}
                          />
                        </span>
                      </p>
                      {(dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.MONTH ||
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.WEEK) && (
                        <p className="mb-2">
                          <span
                            className={`badge bg-${
                              purchasesMemberDiff >= 0
                                ? "success"
                                : "danger"
                            }-subtle text-${
                              purchasesMemberDiff >= 0
                                ? "success"
                                : "danger"
                            }`}
                          >
                            <i
                              className={`ri-arrow-${
                                purchasesMemberDiff >= 0 ? "up" : "down"
                              }-line align-middle me-1`}
                            ></i>
                            {purchasesMemberDiff} vs. {getPreviousPeriodLabel(dateFilter.period)}
                          </span>
                        </p>
                      )}
                      <p className="mb-1 text-muted">
                        <span className="fw-medium text-dark">BPA purchases:</span>{" "}
                        {weeklyBpaPurchases} ({weeklyBpaPurchasesPercentage}%)
                      </p>
                      <p className="mb-1 text-muted">
                        <span className="fw-medium text-dark">Amount spent:</span>{" "}
                        {formatCurrency(weeklyTotalSpent)}
                      </p>
                      {(dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.MONTH ||
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.WEEK) && (
                        <p className="mb-2">
                          <span
                            className={`badge bg-${
                              purchasesAmountSpentDiff >= 0
                                ? "success"
                                : "danger"
                            }-subtle text-${
                              purchasesAmountSpentDiff >= 0
                                ? "success"
                                : "danger"
                            }`}
                          >
                            <i
                              className={`ri-arrow-${
                                purchasesAmountSpentDiff >= 0 ? "up" : "down"
                              }-line align-middle me-1`}
                            ></i>
                            {formatCurrency(purchasesAmountSpentDiff)} vs. {getPreviousPeriodLabel(dateFilter.period)}
                          </span>
                        </p>
                      )}
                      <p className="mb-0 text-muted">
                        <span className="fw-medium text-dark">BPA amount:</span>{" "}
                        {formatCurrency(weeklyBpaAmountSpent)} ({weeklyBpaAmountPercentage}%)
                      </p>
                    </div>
                  </CardBody>
                  <CardFooter className="text-center">
                    <Link
                      onClick={() => {
                        if (shouldUseMonthlyEndpoint(dateFilter)) {
                          dispatch(
                            getMonthlyGeneralCustomerOrderCounts({
                              ...dateFilterParams,
                              download: true,
                            })
                          );
                        } else {
                          dispatch(
                            getWeeklyGeneralCustomerOrderCounts({
                              ...dateFilterParams,
                              download: true,
                            })
                          );
                        }
                        dispatch(
                          getWeeklyTotalPurchases({
                            ...dateFilterParams,
                            download: true,
                          })
                        );
                      }}
                      className="text-decoration-none"
                    >
                      <i className="ri-download-2-line align-middle me-2"></i>
                      Download details
                    </Link>
                  </CardFooter>
                </Card>
              </Col>
              <Col xl={3} md={6}>
                <Card className="card-height-100">
                  <CardBody>
                    <div className="d-flex align-items-center mb-4">
                      <div className="flex-grow-1 ps-3">
                        <h5 className="text-muted text-uppercase fs-13 mb-0">
                          Supplier Orders {statsPeriodLabel}
                        </h5>
                      </div>
                    </div>
                    <div className="ps-4">
                      <p className="mb-1 text-muted">
                        <span className="fw-medium text-dark">Total orders:</span>{" "}
                        <span className="counter-value text-dark">
                          <CountUp
                            start={0}
                            end={supplierTotalOrders}
                            decimals={0}
                            duration={4}
                          />
                        </span>
                      </p>
                      {(dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.MONTH ||
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.WEEK) && (
                        <p className="mb-2">
                          <span
                            className={`badge bg-${
                              supplierTotalOrdersDiff >= 0
                                ? "success"
                                : "danger"
                            }-subtle text-${
                              supplierTotalOrdersDiff >= 0
                                ? "success"
                                : "danger"
                            }`}
                          >
                            <i
                              className={`ri-arrow-${
                                supplierTotalOrdersDiff >= 0 ? "up" : "down"
                              }-line align-middle me-1`}
                            ></i>
                            {supplierTotalOrdersDiff} vs. {getPreviousPeriodLabel(dateFilter.period)}
                          </span>
                        </p>
                      )}
                      <p className="mb-1 text-muted">
                        <span className="fw-medium text-dark">Cancelled orders:</span>{" "}
                        {supplierCancelledCount} ({supplierCancelledPercentage}%)
                      </p>
                      {(dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.MONTH ||
                        dateFilter.period === DASHBOARD_DATE_FILTER_PERIODS.WEEK) && (
                        <p className="mb-2">
                          <span
                            className={`badge bg-${
                              supplierCancelledDiff <= 0
                                ? "success"
                                : "danger"
                            }-subtle text-${
                              supplierCancelledDiff <= 0
                                ? "success"
                                : "danger"
                            }`}
                          >
                            <i
                              className={`ri-arrow-${
                                supplierCancelledDiff <= 0 ? "down" : "up"
                              }-line align-middle me-1`}
                            ></i>
                            {supplierCancelledDiff} vs. {getPreviousPeriodLabel(dateFilter.period)}
                          </span>
                        </p>
                      )}
                      <p className="mb-0 text-muted">
                        <span className="fw-medium text-dark">Members Orders Cancelled:</span>{" "}
                        {supplierCancelledMembersOrders} ({supplierMembersCancelledPercentage}%)
                      </p>
                    </div>
                  </CardBody>
                  <CardFooter className="text-center">
                    <Link
                      onClick={() =>
                        dispatch(
                          getGeneralSupplierOrderCounts({
                            ...dateFilterParams,
                            download: true,
                          })
                        )
                      }
                      className="text-decoration-none"
                    >
                      <i className="ri-download-2-line align-middle me-2"></i>
                      Download details
                    </Link>
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <div className="h-100">
                <Row>
                  <Col md={12} xl={6}>
                    <WeeklyUploads
                      key={`weekly-uploads-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                  <Col md={12} xl={6}>
                    <WeeklyProfitsByChannel
                      key={`weekly-profits-by-channel-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                  <Col md={12} xl={6}>
                    <WeeklyTotalOrderAmount
                      key={`weekly-total-order-amount-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                  <Col md={12} xl={6}>
                    <WeeklyTotalMemberPurchases
                      key={`weekly-total-member-purchases-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                  <Col md={12} xl={6}>
                    <WeeklyNewSuppliers
                      key={`weekly-new-suppliers-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                  <Col md={12} xl={6}>
                    <SubscriptionStats
                      key={`subscription-stats-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                  {/*
                  <Col md={12} xl={6}>
                    <TopSellerSuppliers
                      key={`top-seller-suppliers-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                  <Col md={12} xl={6}>
                    <TopSellerProducts
                      key={`top-seller-products-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                  */}
                  {/*
                  <Col md={12} xl={6}>
                    <TopMembersPurchases />
                  </Col>
                  */}
                  <Col md={12} xl={6}>
                    <TopProductMargins
                      key={`top-product-margins-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>

                  {/*
                  <Col md={12} xl={6}>
                    <WeeklyGeneralSupplierOrder />
                  </Col>
                  <Col md={12} xl={6}>
                    <WeeklyGeneralCustomerOrder />
                  </Col>
                  */}
                  <Col md={12} xl={6}>
                    <WeeklyRefunds
                      key={`weekly-refunds-${chartResetKey}`}
                      dateFilter={dateFilter}
                    />
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
